import TenantConfigurationEditor from './TenantConfigurationEditor.vue';
import BlockingLoaderOverlay from '@/components/config_components/BlockingLoaderOverlay/BlockingLoaderOverlay.vue';
import { PORTFOLIO_COLORS, text } from './company_portfolio_common';
import {
  case_code,
  case_title,
  created_date,
  format_currency,
  is_active_case,
  is_high_risk,
  is_sla_at_risk,
  leakage_amount,
  risk_score,
  sla_label
} from './company_portfolio_formatters';
import { fetch_portfolio_data } from './company_portfolio_loading';
import { portfolio_domains } from './company_portfolio_scope_helpers';

function opened_this_month(cases) {
  const now = new Date();
  return cases.filter((record) => {
    const date = created_date(record);
    return date
      && date.getFullYear() === now.getFullYear()
      && date.getMonth() === now.getMonth();
  }).length;
}

function metric_rows(active_cases) {
  const high_risk = active_cases.filter(is_high_risk).length;
  const leakage = active_cases.reduce((total, record) => total + leakage_amount(record), 0);
  return [
    { label: 'Active Cases', value: active_cases.length.toLocaleString('en-ZA'), change: `+${opened_this_month(active_cases).toLocaleString('en-ZA')} this month`, change_class: 'is-positive' },
    { label: 'SLA At Risk', value: active_cases.filter(is_sla_at_risk).length.toLocaleString('en-ZA'), change: '7-day window', change_class: '' },
    { label: 'High Risk Band', value: high_risk.toLocaleString('en-ZA'), change: `${active_cases.length ? Math.round((high_risk / active_cases.length) * 100) : 0}% of active`, change_class: 'is-positive' },
    { label: 'Leakage Flagged', value: format_currency(leakage), change: 'Current active cases', change_class: '' }
  ];
}

function recent_escalations(active_cases) {
  return active_cases
    .filter((record) => is_high_risk(record) || is_sla_at_risk(record))
    .sort((left, right) => risk_score(right) - risk_score(left)
      || (created_date(right)?.getTime() || 0) - (created_date(left)?.getTime() || 0))
    .slice(0, 4)
    .map((record) => ({
      case_code: case_code(record),
      title: case_title(record),
      scope: text(record.portfolio_project_name) || 'Portfolio',
      risk: risk_score(record),
      sla: sla_label(record),
      sla_danger: is_sla_at_risk(record)
    }));
}

function tenant_mix_entries(projects = [], results = []) {
  return projects.map((project, index) => ({
    name: text(project?.name || project?.project_name || 'Unnamed tenant'),
    active: results[index]?.records?.filter(is_active_case).length || 0,
    color: PORTFOLIO_COLORS[index % PORTFOLIO_COLORS.length]
  }));
}

export default {
  name: 'CompanyPortfolio',
  components: {
    BlockingLoaderOverlay,
    TenantConfigurationEditor,
  },
  data: () => ({
    loading_portfolio: false,
    portfolio_error: '',
    portfolio_cases: [],
    coordinator_scopes: [],
    tenant_configs: [],
    role_group_configs: [],
    tenant_projects: [],
    show_tenant_configuration_editor: false
  }),
  computed: {
    active_cases() {
      return this.portfolio_cases.filter(is_active_case);
    },
    metrics() {
      return metric_rows(this.active_cases);
    },
    recent_escalations() {
      return recent_escalations(this.active_cases);
    },
    portfolio_domains() {
      return portfolio_domains(this.active_cases);
    },
    tenant_mix() {
      return this.tenant_configs.map((tenant, index) => ({
        name: tenant.name,
        active: tenant.active,
        color: PORTFOLIO_COLORS[index % PORTFOLIO_COLORS.length]
      }));
    },
    page_loading_message() {
      return 'Loading portfolio data...';
    },
    page_loading_detail() {
      return 'Fetching portfolio cases, coordinator scopes, and tenant configuration.';
    }
  },
  mounted() {
    this.load_portfolio_data();
  },
  methods: {
    async load_portfolio_data() {
      this.loading_portfolio = true;
      this.portfolio_error = '';
      try {
        const { projects, results, scopes, role_group_configs } = await fetch_portfolio_data();
        this.tenant_projects = projects;
        this.portfolio_cases = results.flatMap((result) => result.records);
        this.coordinator_scopes = scopes;
        this.tenant_configs = tenant_mix_entries(projects, results);
        this.role_group_configs = role_group_configs;
      } catch {
        this.portfolio_cases = [];
        this.coordinator_scopes = [];
        this.tenant_configs = [];
        this.role_group_configs = [];
        this.portfolio_error = 'Portfolio data could not be loaded.';
      } finally {
        this.loading_portfolio = false;
      }
    },
    open_tenant_configuration_editor() {
      this.show_tenant_configuration_editor = true;
    },
    close_tenant_configuration_editor() {
      this.show_tenant_configuration_editor = false;
    },
    async refresh_tenant_configuration() {
      this.show_tenant_configuration_editor = false;
      await this.load_portfolio_data();
    }
  }
};
